import { FlexRow } from '@/components/common/Flex';
import { Text } from '@/components/common/Typography';
import { toDollarsWithCents } from '@/util/currency';
import { FlexProps, Spinner, VStack } from '@chakra-ui/react';
import { Goal } from '@common/goals';
import { getGoalBalanceForSwitcherType } from '@common/react/goals';
import { useCallback } from 'react';
import { FlexCol } from '../common/Flex';
import LoadingWrapper from '../common/LoadingWrapper';
import { hoverStyleProps } from '../common/styleHelpers';
import GoalLockup from './GoalLockup';
import { useTransferDestinationGoals } from './helpers';
import OnetimeTransactionCardLayout from './TransactionCardLayout';

export interface TransactionDestinationGoalCardProps extends FlexProps {
    title: string;
    subtitle: string;
    sourceGoal: Goal;
    destinationGoal: Goal | undefined;
    onChangeDestinationGoal: (destinationGoal: Goal | undefined) => void;
    onBack?: VoidFunction;
    onNext: VoidFunction;
    onClose: VoidFunction;
}

const TransactionDestinationGoalCard = ({
    title,
    subtitle,
    sourceGoal,
    destinationGoal,
    onChangeDestinationGoal,
    onBack,
    onNext,
    onClose,
    ...props
}: TransactionDestinationGoalCardProps) => {
    const { isLoading, goals } = useTransferDestinationGoals({
        sourceGoal,
    });

    return (
        <OnetimeTransactionCardLayout
            {...props}
            title={title}
            subtitle={subtitle}
            onBack={onBack}
            onNext={onNext}
            isNextDisabled={!destinationGoal}
            onClose={onClose}
        >
            <LoadingWrapper isLoading={isLoading}>
                <FlexCol {...props}>
                    <VStack alignItems="stretch">
                        {goals.length === 0 ? (
                            <Text color="textMuted">{`No available goals. Once you've invested in a goal, it will appear here.`}</Text>
                        ) : (
                            goals.map((goal) => (
                                <DestinationGoalItem
                                    key={goal.id}
                                    goal={goal}
                                    isSelected={goal.id === destinationGoal?.id}
                                    onChange={onChangeDestinationGoal}
                                />
                            ))
                        )}
                    </VStack>
                </FlexCol>
            </LoadingWrapper>
        </OnetimeTransactionCardLayout>
    );
};

interface DestinationGoalItemProps {
    goal: Goal;
    isSelected: boolean;
    onChange: (goal?: Goal) => void;
}

function DestinationGoalItem({
    goal,
    isSelected,
    onChange,
}: DestinationGoalItemProps) {
    const handleClick = useCallback(() => {
        onChange(isSelected ? undefined : goal);
    }, [onChange, isSelected, goal]);
    const value = getGoalBalanceForSwitcherType(goal, 'SHARED') ?? 0;

    return (
        <FlexCol
            p={4}
            border="1px solid"
            borderColor={isSelected ? 'blue.400' : 'border'}
            bg={isSelected ? 'blue.50' : 'white'}
            rounded="md"
            {...hoverStyleProps({
                shadow: 'sm',
                bg: isSelected ? 'blue.50' : 'gray.50',
            })}
            _active={{
                bg: 'blue.50',
                borderColor: 'blue.400',
            }}
            onClick={handleClick}
        >
            <FlexRow justify="space-between" align="center">
                <GoalLockup goal={goal} pr={4} />
                {value === null ? (
                    <Spinner size="sm" color="gray.500" />
                ) : (
                    <Text fontWeight="semibold">
                        {toDollarsWithCents(value)}
                    </Text>
                )}
            </FlexRow>
        </FlexCol>
    );
}

export default TransactionDestinationGoalCard;
